import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import Loader from "../components/Loader";

function CalendarPlanner() {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Modal State for Details
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ date: "", items: [] });

  // Current Month Display
  const currentMonthName = selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  useEffect(() => {
      fetchEvents();
      // eslint-disable-next-line
  }, [selectedDate]);

  const fetchEvents = async () => {
      setLoading(true);
      const startStr = selectedDate.toISOString().slice(0, 7); // YYYY-MM
      let tempEvents = {};

      // Helper function to push data
      const addEvent = (date, type, title, color, details) => {
          if (!date) return;
          const d = date.split('T')[0]; // Ensure YYYY-MM-DD
          if (d.startsWith(startStr)) {
              if (!tempEvents[d]) tempEvents[d] = [];
              tempEvents[d].push({ type, title, color, details });
          }
      };

      try {
          // 1. 🔵 Sales Visits
          const qVisits = await getDocs(collection(db, "sales_reports"));
          qVisits.forEach(d => addEvent(d.data().createdAt, "Visit", d.data().hospital, "#3498db", `By: ${d.data().userName}`));

          // 2. 🔴 Payment Dues (Open only)
          const qDues = await getDocs(query(collection(db, "payment_dues"), where("status", "==", "Open")));
          qDues.forEach(d => addEvent(d.data().dueDate, "Due", `₹${d.data().amount}`, "#e74c3c", `${d.data().hospitalName}`));

          // 3. 🟢 PMS / Service
          const qPMS = await getDocs(collection(db, "pms_reports"));
          qPMS.forEach(d => addEvent(d.data().date, "PMS", d.data().hospital, "#27ae60", `Type: ${d.data().type}`));

          // 4. 🟡 Tasks (Pending) - NEW 🔥
          const qTasks = await getDocs(query(collection(db, "tasks"), where("status", "!=", "Completed")));
          qTasks.forEach(d => addEvent(d.data().dueDate, "Task", d.data().title, "#f39c12", `Assigned to: ${d.data().assignedTo}`));

          // 5. 🟣 Couriers - NEW 🔥
          const qCourier = await getDocs(collection(db, "couriers"));
          qCourier.forEach(d => addEvent(d.data().date, "Courier", d.data().partyName, "#9b59b6", `Docket: ${d.data().docketNo}`));

          // 6. ⚫ Service Calls (Pending) - NEW 🔥
          const qCalls = await getDocs(query(collection(db, "service_calls"), where("status", "==", "Pending")));
          qCalls.forEach(d => addEvent(d.data().date || d.data().createdAt, "Call", d.data().hospitalName, "#34495e", `Issue: ${d.data().issue}`));

      } catch (e) {
          console.error("Error fetching calendar data:", e);
      }

      setEvents(tempEvents);
      setLoading(false);
  };

  // Helper to change month
  const changeMonth = (offset) => {
      const newDate = new Date(selectedDate);
      newDate.setMonth(newDate.getMonth() + offset);
      setSelectedDate(newDate);
  };

  // Handle Click on Date Box
  const handleDateClick = (dateKey, dayEvents) => {
      if (dayEvents && dayEvents.length > 0) {
          setModalData({ date: dateKey, items: dayEvents });
          setShowModal(true);
      }
  };

  // Generate Calendar Grid
  const renderCalendar = () => {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth();
      
      const firstDay = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      let days = [];
      for (let i = 0; i < firstDay; i++) days.push(<div key={`empty-${i}`} style={dayBoxEmpty}></div>);
      
      for (let d = 1; d <= daysInMonth; d++) {
          const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const dayEvents = events[dateKey] || [];

          days.push(
              <div 
                  key={d} 
                  style={{...dayBox, cursor: dayEvents.length > 0 ? "pointer" : "default", borderColor: dayEvents.length > 0 ? "#3498db" : "#eee"}}
                  onClick={() => handleDateClick(dateKey, dayEvents)}
              >
                  <div style={dateNum}>{d}</div>
                  <div style={{overflowY:"hidden", height:"60px"}}>
                      {dayEvents.slice(0, 3).map((ev, idx) => ( // Show max 3, click for more
                          <div key={idx} style={{...eventTag, backgroundColor: ev.color}}>
                              {getIcon(ev.type)} {ev.title}
                          </div>
                      ))}
                      {dayEvents.length > 3 && <div style={{fontSize:"10px", color:"gray"}}>+ {dayEvents.length - 3} more...</div>}
                  </div>
              </div>
          );
      }
      return days;
  };

  const getIcon = (type) => {
      switch(type) {
          case 'Due': return '💰';
          case 'Visit': return '🤝';
          case 'PMS': return '🔧';
          case 'Task': return '📋';
          case 'Courier': return '📦';
          case 'Call': return '📞';
          default: return '•';
      }
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif"}}>
      
      {/* Header */}
      <div style={headerStyle}>
          <button onClick={()=>changeMonth(-1)} style={navBtn}>◀ Prev</button>
          <h2 style={{margin:0, color:"#2c3e50"}}>{currentMonthName}</h2>
          <button onClick={()=>changeMonth(1)} style={navBtn}>Next ▶</button>
      </div>

      {/* Grid */}
      {loading ? <Loader /> : (
          <div style={gridStyle}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} style={{fontWeight:"bold", textAlign:"center", padding:"10px", background:"#f1f5f9", color:"#555"}}>{d}</div>
              ))}
              {renderCalendar()}
          </div>
      )}
      
      {/* Legend */}
      <div style={{marginTop:"20px", display:"flex", flexWrap:"wrap", gap:"15px", fontSize:"12px", background:"white", padding:"10px", borderRadius:"8px"}}>
          <LegendItem color="#3498db" label="Sales Visit" icon="🤝" />
          <LegendItem color="#e74c3c" label="Payment Due" icon="💰" />
          <LegendItem color="#27ae60" label="PMS / Service" icon="🔧" />
          <LegendItem color="#f39c12" label="Pending Task" icon="📋" />
          <LegendItem color="#9b59b6" label="Courier" icon="📦" />
          <LegendItem color="#34495e" label="Service Call" icon="📞" />
      </div>

      {/* CLICK POPUP MODAL */}
      {showModal && (
          <div style={modalOverlay}>
              <div style={modalContent}>
                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:"1px solid #eee", paddingBottom:"10px", marginBottom:"10px"}}>
                      <h3 style={{margin:0, color:"#2c3e50"}}>📅 {new Date(modalData.date).toDateString()}</h3>
                      <button onClick={()=>setShowModal(false)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
                  </div>
                  <div style={{maxHeight:"400px", overflowY:"auto"}}>
                      {modalData.items.map((ev, i) => (
                          <div key={i} style={{padding:"10px", borderLeft:`4px solid ${ev.color}`, background:"#f9f9f9", marginBottom:"10px", borderRadius:"4px"}}>
                              <div style={{fontWeight:"bold", color:"#333"}}>{getIcon(ev.type)} {ev.type}</div>
                              <div style={{fontSize:"14px", margin:"2px 0"}}>{ev.title}</div>
                              <div style={{fontSize:"12px", color:"gray"}}>{ev.details}</div>
                          </div>
                      ))}
                  </div>
              </div>
          </div>
      )}

    </div>
  );
}

// Sub Component for Legend
const LegendItem = ({color, label, icon}) => (
    <div style={{display:"flex", alignItems:"center", gap:"5px"}}>
        <div style={{width:12, height:12, background:color, borderRadius:"3px"}}></div> 
        <span>{icon} {label}</span>
    </div>
);

// Styles
const headerStyle={display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px", background:"white", padding:"15px", borderRadius:"8px", boxShadow:"0 2px 5px rgba(0,0,0,0.05)"};
const navBtn={padding:"8px 15px", background:"#2c3e50", color:"white", border:"none", borderRadius:"5px", cursor:"pointer"};
const gridStyle={display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:"5px", background:"white", padding:"10px", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"};
const dayBox={minHeight:"100px", border:"1px solid #f0f0f0", padding:"5px", borderRadius:"4px", background:"white", transition:"0.2s"};
const dayBoxEmpty={background:"#f9f9f9"};
const dateNum={fontWeight:"bold", fontSize:"14px", marginBottom:"5px", color:"#555", textAlign:"right"};
const eventTag={fontSize:"10px", color:"white", padding:"3px 5px", borderRadius:"3px", marginBottom:"3px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"20px", borderRadius:"10px", width:"400px", boxShadow:"0 5px 15px rgba(0,0,0,0.3)"};

export default CalendarPlanner;